import React, { useRef, useEffect, useState } from 'react';

/*
  MCTheme V3 — DOM Mutation Approach
  React inline styles beat CSS !important.
  So we use MutationObserver + direct style mutation
  to retheme components AFTER React renders them.
  Also injects entrance animations via class injection.
*/

const GLASS_BG = "#2C2E33";
const GLASS_BG_HOVER = "#33363D";
const GLASS_BORDER = "rgba(217,219,221,0.12)";
const ACCENT = "#FAFAFA";
const ACCENT_LIGHT = "#6B83D4";

function applyTheme(root) {
  if (!root) return;

  // ── Fix backgrounds: make cards/panels transparent ──
  const allDivs = root.querySelectorAll("div");
  allDivs.forEach((el, i) => {
    const bg = el.style.background || el.style.backgroundColor;
    if (!bg) return;

    // Skip the mc-theme root itself
    if (el.classList.contains("mc-theme")) return;
    // Skip elements that are already themed
    if (el.dataset.mcThemed) return;
    // Skip dropdown popups and their children
    if (el.classList.contains("cf-dropdown-popup") || el.closest(".cf-dropdown-popup")) return;
    // Skip elements already using our solid brand backgrounds
    if (bg === "#2C2E33" || bg === "rgb(44, 46, 51)" || bg === "#33363D" || bg === "rgb(51, 54, 61)") return;

    // Make opaque whites transparent
    if (
      bg.includes("rgba(255, 255, 255, 0.15)") ||
      bg.includes("rgba(255, 255, 255, 0.1)") ||
      bg.includes("rgba(255,255,255,0.15)") ||
      bg.includes("rgba(255,255,255,0.1)") ||
      bg === "white" || bg === "#fff" || bg === "#FFF" ||
      bg === "#ffffff" || bg === "#FFFFFF" ||
      bg === "rgb(255, 255, 255)" ||
      bg.includes("rgba(255, 255, 255, 0.25)") ||
      bg.includes("rgba(255, 255, 255, 0.08)")
    ) {
      el.style.background = GLASS_BG;
      el.dataset.mcThemed = "1";
    }

    // Make solid light backgrounds semi-transparent
    if (
      bg === "#F5F6FA" || bg === "#EFF0F6" || bg === "#FAFAFA" ||
      bg === "rgb(245, 246, 250)" || bg === "rgb(239, 240, 246)" ||
      bg === "rgb(250, 250, 250)" || bg === "#f5f6fa" || bg === "#eff0f6"
    ) {
      el.style.background = "transparent";
      el.dataset.mcThemed = "1";
    }
  });

  // ── Fix borders ──
  allDivs.forEach(el => {
    const styles = ["border", "borderTop", "borderBottom", "borderLeft", "borderRight"];
    styles.forEach(prop => {
      const val = el.style[prop];
      if (val && (val.includes("rgba(255") || val.includes("rgb(") || val.includes("#"))) {
        if (!val.includes("rgba(0,0,0,0.0") && !val.includes(ACCENT)) {
          el.style[prop] = val.replace(/rgba?\([^)]+\)|#[0-9a-fA-F]+/, GLASS_BORDER);
        }
      }
    });
    // Also fix borderColor directly
    if (el.style.borderColor && !el.style.borderColor.includes(ACCENT) && !el.style.borderColor.includes("rgb(22")) {
      el.style.borderColor = GLASS_BORDER;
    }
  });

  // ── Fix box shadows — make them softer ──
  allDivs.forEach(el => {
    if (el.style.boxShadow && el.style.boxShadow.includes("rgba(64,89,173")) {
      // Already themed shadow, leave it
    } else if (el.style.boxShadow && el.style.boxShadow.length > 5) {
      el.style.boxShadow = `0 8px 32px rgba(64,89,173,0.05), inset 0 1px 0 rgba(255,255,255,0.06)`;
    }
  });

  // ── Add entrance animation classes to direct children ──
  const directChildren = root.querySelector(":scope > div");
  if (directChildren) {
    const sections = directChildren.children;
    for (let i = 0; i < sections.length; i++) {
      const child = sections[i];
      if (!child.dataset.mcAnimated) {
        child.dataset.mcAnimated = "1";
        child.style.opacity = "0";
        child.style.transform = "translateY(8px)";
        child.style.transition = `opacity 0.4s cubic-bezier(.32,1,.23,1) ${i * 0.05}s, transform 0.4s cubic-bezier(.32,1,.23,1) ${i * 0.05}s`;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            child.style.opacity = "1";
            child.style.transform = "translateY(0)";
          });
        });
      }
    }
  }

  // ── Make buttons prettier ──
  const buttons = root.querySelectorAll("button");
  buttons.forEach(btn => {
    if (btn.dataset.mcThemed) return;
    btn.dataset.mcThemed = "1";
    // Add transition
    const existing = btn.style.transition || "";
    btn.style.transition = existing + ", transform 0.15s cubic-bezier(.32,1,.23,1), filter 0.15s ease";
    // Soften border radius
    const br = parseInt(btn.style.borderRadius) || 0;
    if (br > 0 && br < 20) {
      btn.style.borderRadius = "8px";
    }
    btn.addEventListener("mouseenter", () => {
      btn.style.transform = (btn.style.transform || "") + " translateY(-1px)";
      btn.style.filter = "brightness(1.05)";
    });
    btn.addEventListener("mouseleave", () => {
      btn.style.transform = (btn.style.transform || "").replace(" translateY(-1px)", "");
      btn.style.filter = "";
    });
  });

  // ── Make inputs glass ──
  const inputs = root.querySelectorAll("input, textarea, select");
  inputs.forEach(inp => {
    if (inp.dataset.mcThemed || inp.type === "checkbox" || inp.type === "radio") return;
    if (inp.closest(".cf-dialer-search") || inp.closest(".cf-email-search")) return;
    // Skip inputs already styled with dark background
    const bg = inp.style.background || inp.style.backgroundColor || "";
    if (bg.includes("rgba(255,255,255,0.0") || bg === "transparent" || bg.includes("#2C2E33") || bg.includes("#33363D")) return;
    inp.dataset.mcThemed = "1";
    inp.style.background = "rgba(255,255,255,0.06)";
    inp.style.border = "1px solid rgba(217,219,221,0.12)";
    inp.style.borderRadius = "8px";
    inp.style.transition = "border-color 0.2s, box-shadow 0.2s, background 0.2s";
    inp.addEventListener("focus", () => {
      inp.style.borderColor = ACCENT + "40";
      inp.style.background = "rgba(255,255,255,0.10)";
    });
    inp.addEventListener("blur", () => {
      inp.style.borderColor = "rgba(217,219,221,0.12)";
      inp.style.boxShadow = "none";
      inp.style.background = "rgba(255,255,255,0.06)";
    });
  });
}

export default function MCTheme({ children }) {
  const rootRef = useRef(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setShow(true));
  }, []);

  useEffect(() => {
    if (!rootRef.current) return;

    // Apply theme immediately
    const timer = setTimeout(() => applyTheme(rootRef.current), 50);

    // Watch for DOM changes (React re-renders) and re-apply
    const observer = new MutationObserver(() => {
      applyTheme(rootRef.current);
    });
    observer.observe(rootRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [show]);

  return (
    <div
      ref={rootRef}
      className="mc-theme"
      style={{
        opacity: show ? 1 : 0,
        transform: show ? "none" : "translateY(6px)",
        transition: "all 0.4s cubic-bezier(.32,1,.23,1)",
      }}
    >
      <style>{`
        .mc-theme ::-webkit-scrollbar { width: 5px; height: 5px; }
        .mc-theme ::-webkit-scrollbar-track { background: transparent; }
        .mc-theme ::-webkit-scrollbar-thumb { background: rgba(64,89,173,0.10); border-radius: 3px; }
        .mc-theme ::-webkit-scrollbar-thumb:hover { background: rgba(250,250,250,0.1); }
        .mc-theme input::placeholder, .mc-theme textarea::placeholder { color: #8b8fa3 !important; opacity: 0.6 !important; }
        .mc-theme * { font-family: -apple-system, BlinkMacSystemFont, "SF Pro", system-ui, sans-serif; }
        .mc-theme .cf-dropdown-popup { background: #2C2E33 !important; backdrop-filter: none !important; -webkit-backdrop-filter: none !important; }

        /* Glow effect on green call button */
        .mc-theme button[style*="background: rgb(22, 163, 74)"],
        .mc-theme button[style*="background: #16a34a"],
        .mc-theme button[style*="background: #22c55e"],
        .mc-theme button[style*="background: #22C55E"] {
          box-shadow: 0 4px 20px rgba(22, 163, 74, 0.3) !important;
          animation: mcCallGlow 2s ease-in-out infinite !important;
        }
        @keyframes mcCallGlow {
          0%, 100% { box-shadow: 0 4px 20px rgba(22,163,74,0.25); }
          50% { box-shadow: 0 6px 30px rgba(22,163,74,0.4); }
        }

        /* Red end call glow */
        .mc-theme button[style*="background: #ef4444"],
        .mc-theme button[style*="background: rgb(239, 68, 68)"] {
          box-shadow: 0 4px 20px rgba(239, 68, 68, 0.25) !important;
        }

        /* Pulse animation for "CALL IN PROGRESS" */
        .mc-theme [style*="color: rgb(22, 163, 74)"][style*="letter-spacing"] {
          animation: mcPulseText 1.5s ease-in-out infinite !important;
        }
        @keyframes mcPulseText {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>
      {children}
    </div>
  );
}
